import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Skeleton,
  Snackbar,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableFooter,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Box,
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Search,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import HeaderSection from "../HeaderSection";
import FilterSection from "../FilterSection";
import { useTablaGestionConciliaciones } from "../../hooks/useTablaGestionConciliaciones";
import { guardarObservacionPeajeService } from "modules/finops/conciliaciones/services/peajesConciliacionService";
import { summarizeDetailSteps } from "../../utils/stepTotals";
import { exportToExcel } from "../../utils/exportToExcel";
import {
  reconciliationColumns,
  reconciliationColor,
  reconciliationTableSx,
} from "../../utils/reconciliationPresentation";

// ============================================================
// PALETA
// ============================================================
const CREMA_FONDO = "#fef9e7";
const CREMA_HEADER = "#fef3c7";
const CREMA_BORDER = "#fde68a";
const AMBAR_BORDE = "#f59e0b";

const TEXTO_SUBTOTAL = "#111827";
const TEXTO_TOTAL = "#111827";

const entriesConfig = {
  defaultValue: 200,
  entries: [200],
};

// ============================================================
// ESTILOS DE CELDA
// ============================================================
const cellStyle = {
  fontSize: "0.82rem",
  color: "#1f2937",
  whiteSpace: "nowrap",
  padding: "8px 14px",
  letterSpacing: "0.1px",
};

const subtotalCellStyle = {
  ...cellStyle,
  color: TEXTO_SUBTOTAL,
  fontWeight: 700,
};

const totalCellStyle = {
  ...cellStyle,
  color: TEXTO_TOTAL,
  fontWeight: 700,
  fontSize: "0.82rem",
};

const dayCellStyle = {
  ...cellStyle,
  textAlign: "center",
  fontWeight: 700,
  fontSize: "0.75rem",
  color: "#374151",
};

const TablaGestionConciliaciones = forwardRef((props, ref) => {
  const {
    error,
    appliedFilters,
    concesiones,
    peajes,
    selectedConcesion,
    selectedPeaje,
    selectedFechaInicio,
    selectedFechaFin,
    loading,
    loadingPeajes,
    showResults,
    datosTabla,
    datosResumen: resumenRecibido,
    totalesGenerales: totalesRecibidos,
    resumenDiferencias,
    setSelectedConcesion,
    setSelectedPeaje,
    setSelectedFechaInicio,
    setSelectedFechaFin,
    handleBuscar,
    handleLimpiarTodo,
  } = useTablaGestionConciliaciones();

  const pasosCalculados = useMemo(() => summarizeDetailSteps(datosTabla), [datosTabla]);
  const totalesGenerales = useMemo(
    () => ({
      ...totalesRecibidos,
      totalPasos: pasosCalculados.total ?? totalesRecibidos?.totalPasos,
    }),
    [totalesRecibidos, pasosCalculados]
  );
  const datosResumen = useMemo(
    () =>
      resumenRecibido.map((row) => ({
        ...row,
        totalPasos: pasosCalculados.byPeaje.get(row.peaje) ?? row.totalPasos ?? row.total_pasos,
      })),
    [resumenRecibido, pasosCalculados]
  );

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(200);
  useEffect(() => setPage(0), [datosTabla]);
  const [observacionModal, setObservacionModal] = useState(false);
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [observacionTexto, setObservacionTexto] = useState("");
  const [observacionesGuardadas, setObservacionesGuardadas] = useState({});
  const [guardandoObservacion, setGuardandoObservacion] = useState(false);
  const [errorObservacion, setErrorObservacion] = useState("");
  const [avisoObservacion, setAvisoObservacion] = useState("");
  const envioObservacion = useRef(null);
  useEffect(() => () => envioObservacion.current?.abort(), []);
  const claveObservacion = (row) =>
    JSON.stringify([row?.concesion || appliedFilters?.concesion, row?.fecha, row?.peaje]);
  const textoObservacion = (row) =>
    observacionesGuardadas[claveObservacion(row)] ??
    (typeof row?.observacion === "string" ? row.observacion : "");

  // ------------------------------------------------------------
  // Formato de moneda
  // ------------------------------------------------------------
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "$ -";
    const num = Number(value);
    if (isNaN(num)) return "$ -";
    const formatted = Math.abs(num).toLocaleString("es-CO", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return num < 0 ? `-$ ${formatted}` : `$ ${formatted}`;
  };

  // ------------------------------------------------------------
  // Formato fecha corta
  // ------------------------------------------------------------
  const formatFechaCorta = (fechaStr) => {
    if (!fechaStr) return "";
    try {
      const d = new Date(fechaStr + "T00:00:00");
      const dia = String(d.getDate()).padStart(2, "0");
      const mes = d.toLocaleString("es-CO", { month: "short" }).replace(".", "");
      return `${dia}-${mes}`;
    } catch {
      return fechaStr;
    }
  };

  // ------------------------------------------------------------
  // Render de dinero
  // ------------------------------------------------------------
  const renderMoney = (value, tipoFila = "detalle") => {
    const isSubtotal = tipoFila === "subtotal";
    const isTotal = tipoFila === "total";
    const style = isTotal ? totalCellStyle : isSubtotal ? subtotalCellStyle : cellStyle;
    const color = isTotal ? TEXTO_TOTAL : isSubtotal ? TEXTO_SUBTOTAL : null;
    const fontWeight = isTotal || isSubtotal ? 700 : 500;

    if (value === null || value === undefined || value === "" || Number(value) === 0) {
      return (
        <MDTypography
          sx={{
            ...style,
            color: color || "#94a3b8",
            fontWeight,
            padding: 0,
            border: "none",
            fontSize: "0.82rem",
          }}
        >
          $ -
        </MDTypography>
      );
    }
    return (
      <MDTypography
        sx={{
          ...style,
          fontWeight,
          padding: 0,
          border: "none",
          fontSize: "0.82rem",
          color: color || "inherit",
        }}
      >
        {formatCurrency(value)}
      </MDTypography>
    );
  };

  // ------------------------------------------------------------
  // Acciones
  // ------------------------------------------------------------
  const formatPasos = (value) => (value == null ? "—" : Number(value).toLocaleString("es-CO"));
  const abrirModalObservacion = (row) => {
    if (textoObservacion(row).trim()) return;
    setFilaSeleccionada({ ...row, concesion: row.concesion || appliedFilters?.concesion });
    setObservacionTexto(textoObservacion(row));
    setErrorObservacion("");
    setAvisoObservacion("");
    setObservacionModal(true);
  };

  const cerrarModalObservacion = () => {
    if (envioObservacion.current) return;
    setObservacionModal(false);
    setFilaSeleccionada(null);
    setObservacionTexto("");
    setErrorObservacion("");
  };

  const guardarObservacion = async () => {
    if (
      !filaSeleccionada ||
      envioObservacion.current ||
      !observacionTexto.trim() ||
      textoObservacion(filaSeleccionada).trim()
    )
      return;
    const controller = new AbortController();
    envioObservacion.current = controller;
    setGuardandoObservacion(true);
    setErrorObservacion("");
    try {
      await guardarObservacionPeajeService(
        {
          concesion: filaSeleccionada.concesion,
          fecha: filaSeleccionada.fecha,
          peaje: filaSeleccionada.peaje,
          observacion: observacionTexto,
        },
        controller.signal
      );
      if (controller.signal.aborted) return;
      setObservacionesGuardadas((prev) => ({
        ...prev,
        [claveObservacion(filaSeleccionada)]: observacionTexto.trim(),
      }));
      setAvisoObservacion("Observación guardada correctamente.");
      setObservacionModal(false);
      setFilaSeleccionada(null);
      setObservacionTexto("");
    } catch (error) {
      if (!controller.signal.aborted)
        setErrorObservacion(
          error.response?.data?.message || error.message || "No se pudo guardar la observación."
        );
    } finally {
      if (envioObservacion.current === controller) envioObservacion.current = null;
      if (!controller.signal.aborted) setGuardandoObservacion(false);
    }
  };

  const pagoFinopsTooltip = (cuadrado) =>
    cuadrado
      ? "Valores cuadrados: los valores de GoPass y la concesión coinciden. Valor a pagar por parte de Financiera."
      : "Valor a pagar por parte de Financiera, autorizado por FinOps";
  const renderPagoFinops = (value, cuadrado = false) => (
    <Tooltip title={pagoFinopsTooltip(cuadrado)} arrow>
      <Typography
        component="span"
        sx={{
          color: cuadrado ? "#166534 !important" : "#b91c1c !important",
          fontWeight: 700,
          fontSize: "0.82rem",
        }}
      >
        {formatCurrency(value)}
      </Typography>
    </Tooltip>
  );

  // ------------------------------------------------------------
  // EXPORTAR A EXCEL (con colores tipo Excel)
  // ------------------------------------------------------------
  const handleExport = () => {
    if (!datosTabla || datosTabla.length === 0) return;

    // 1) Columnas
    const columnasExcel = reconciliationColumns.map(({ key, label }) => ({ key, label }));

    // 2) Filas detalle / subtotal
    const datosExcel = datosTabla.map((row) => ({
      __rowType: row.tipoFila || "detalle",
      __pagoFinops: row.pagoFinops,
      __pagoFinopsCuadrado: row.pagoFinopsCuadrado,
      fecha: formatFechaCorta(row.fecha),
      peaje: row.peaje,
      totalPasos: row.totalPasos == null ? "" : Number(row.totalPasos),
      observacion: row.pagoFinops
        ? pagoFinopsTooltip(row.pagoFinopsCuadrado)
        : row.tipoFila === "subtotal"
        ? ""
        : textoObservacion(row),
      gopassPasosExitosos: formatCurrency(row.gopassPasosExitosos),
      valorConcesion: formatCurrency(row.valorConcesion),
      diferenciaInicial: formatCurrency(row.diferenciaInicial),
      pagosRechazos: formatCurrency(row.pagosRechazos),
      totalGopass: formatCurrency(row.totalGopass),
      diferenciaFinal: formatCurrency(row.diferenciaFinal),
      pagoFinanciera: row.pagoFinops
        ? Number(row.pagoFinanciera)
        : formatCurrency(row.pagoFinanciera),
      diferenciaFinanciera: formatCurrency(row.diferenciaFinanciera),
      valoresAdicionales: formatCurrency(row.valoresAdicionales),
      descuentos: formatCurrency(row.descuentos ?? row.descuento),
    }));

    datosExcel.push({
      __rowType: "total",
      __pagoFinops: totalesGenerales.pagoFinops,
      __pagoFinopsCuadrado: totalesGenerales.pagoFinopsCuadrado,
      fecha: "Total",
      peaje: "TOTALES",
      ...Object.fromEntries(
        columnasExcel
          .slice(2)
          .map(({ key }) => [
            key,
            key === "observacion"
              ? ""
              : key === "totalPasos"
              ? totalesGenerales.totalPasos ?? ""
              : formatCurrency(totalesGenerales[key]),
          ])
      ),
      ...(totalesGenerales.pagoFinops
        ? { pagoFinanciera: Number(totalesGenerales.pagoFinanciera) }
        : {}),
    });

    // 3) Bloque Resumen de Totales
    if (datosResumen && datosResumen.length > 0) {
      datosExcel.push({
        __rowType: "spacer",
        fecha: "",
        peaje: "",
      });
      datosExcel.push({
        __rowType: "sectionHeader",
        fecha: "RESUMEN DE TOTALES",
        peaje: "",
        totalPasos: "TOTAL PASOS",
        pagosRechazos: "TOTAL RECHAZADO",
        descuentos: "TOTAL GOPASS",
      });
      datosResumen.forEach((r) => {
        datosExcel.push({
          __rowType: "resumenRow",
          fecha: "",
          peaje: r.peaje,
          totalPasos: r.totalPasos ?? "",
          pagosRechazos: formatCurrency(r.totalRechazado),
          descuentos: formatCurrency(r.totalPago),
        });
      });
    }

    // 4) Bloque Diferencias (TODAS)
    if (resumenDiferencias && resumenDiferencias.length > 0) {
      datosExcel.push({
        __rowType: "spacer",
        fecha: "",
        peaje: "",
      });
      datosExcel.push({
        __rowType: "sectionHeader",
        fecha: "DIFERENCIAS Y CONCILIACIÓN",
        peaje: "VALOR",
      });

      resumenDiferencias.forEach((d) => {
        datosExcel.push({
          __rowType: "diffRow",
          fecha: d.label,
          peaje: formatCurrency(d.value),
          __isNegative: d.value < 0,
        });
      });
    }

    // 5) Callback de estilos por fila
    const getRowStyle = (row) => {
      const type = row.__rowType;

      if (type === "detalle") {
        return {
          font: { color: { rgb: "1F2937" }, sz: 10 },
        };
      }
      if (type === "total")
        return {
          font: { color: { rgb: "111827" }, bold: true, sz: 10 },
          border: { top: { style: "medium", color: { rgb: "64748B" } } },
        };
      if (type === "subtotal") {
        return {
          font: { color: { rgb: "111827" }, bold: true, sz: 10 },
          border: { bottom: { style: "thin", color: { rgb: "64748B" } } },
        };
      }
      if (type === "spacer") {
        return {
          fill: { fgColor: { rgb: "FFFFFF" } },
          border: {
            top: { style: "none" },
            bottom: { style: "none" },
            left: { style: "none" },
            right: { style: "none" },
          },
        };
      }
      if (type === "sectionHeader") {
        return {
          fill: { fgColor: { rgb: "FEF3C7" } },
          font: { color: { rgb: "78350F" }, bold: true, sz: 11 },
          alignment: { horizontal: "left" },
          border: {
            top: { style: "medium", color: { rgb: "E8C779" } },
            bottom: { style: "medium", color: { rgb: "E8C779" } },
            left: { style: "thin", color: { rgb: "E8C779" } },
            right: { style: "thin", color: { rgb: "E8C779" } },
          },
        };
      }
      if (type === "resumenRow") {
        return {
          fill: { fgColor: { rgb: "FEF9E7" } },
          font: { color: { rgb: "1F2937" }, sz: 10 },
        };
      }
      if (type === "diffRow") {
        const esNegativo = row.__isNegative;
        return {
          fill: { fgColor: { rgb: "FDF3D0" } },
          font: {
            color: { rgb: esNegativo ? "C62828" : "1B5E20" },
            bold: true,
            sz: 10,
          },
        };
      }
      return {};
    };

    exportToExcel({
      data: datosExcel,
      columns: columnasExcel,
      fileName: `Conciliaciones_${appliedFilters?.concesion || selectedConcesion}_${
        new Date().toISOString().split("T")[0]
      }`,
      sheetName: "Conciliaciones",
      getRowStyle,
      headerStyle: {
        fill: { fgColor: { rgb: "F0F4F7" } },
        font: { color: { rgb: "334155" }, bold: true, sz: 11 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: { bottom: { style: "thin", color: { rgb: "DBE3EA" } } },
      },
      getHeaderCellStyle: (column, index) => ({
        fill: { fgColor: { rgb: reconciliationColor(index) } },
        font: { color: { rgb: "111827" }, bold: true, sz: 11 },
        border: {
          bottom: { style: "thin", color: { rgb: "64748B" } },
          ...([5, 8].includes(index)
            ? { right: { style: "medium", color: { rgb: "9BA8B4" } } }
            : {}),
        },
      }),
      getCellStyle: (row, column, index) => ({
        ...(column.key === "totalPasos" ? { numFmt: "#,##0" } : {}),
        ...(["detalle", "subtotal", "total"].includes(row.__rowType)
          ? {
              fill: { fgColor: { rgb: reconciliationColor(index) } },
              font: { color: { rgb: "111827" }, bold: row.__rowType !== "detalle" },
              ...([5, 8].includes(index)
                ? { border: { right: { style: "medium", color: { rgb: "9BA8B4" } } } }
                : {}),
            }
          : {}),
        ...(row.__pagoFinops && column.key === "pagoFinanciera"
          ? {
              font: { color: { rgb: row.__pagoFinopsCuadrado ? "166534" : "B91C1C" }, bold: true },
              numFmt: '"$" #,##0.00',
            }
          : {}),
        alignment: {
          horizontal:
            row.__rowType === "detalle" || row.__rowType === "subtotal" || row.__rowType === "total"
              ? index === 0
                ? "center"
                : index === 1 || column.key === "observacion"
                ? "left"
                : "right"
              : "left",
          vertical: "center",
          wrapText: column.key === "observacion",
        },
      }),
    });
  };

  // ------------------------------------------------------------
  // COLUMNAS
  // ------------------------------------------------------------
  const columns = [
    ...reconciliationColumns.map(({ accessor, label, width }) => ({
      Header: label,
      accessor,
      width,
      align:
        accessor === "dia"
          ? "center"
          : ["peaje", "observacion"].includes(accessor)
          ? "left"
          : "right",
      Cell: ({ value }) => value,
    })),
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "6%",
      align: "center",
      Cell: ({ value }) => value,
    },
  ];

  // ------------------------------------------------------------
  // ROWS PRINCIPALES + FILA TOTALES
  // ------------------------------------------------------------

  const rows = datosTabla.map((row) => {
    const esSubtotal = row.tipoFila === "subtotal";

    const tipoFila = esSubtotal ? "subtotal" : "detalle";
    const s = esSubtotal ? subtotalCellStyle : cellStyle;
    const dayS = esSubtotal ? subtotalCellStyle : dayCellStyle;

    return {
      dia: (
        <MDBox sx={{ ...dayS, padding: 0, border: "none" }}>
          <MDTypography sx={{ ...dayS, padding: 0, border: "none", fontSize: "0.75rem" }}>
            {formatFechaCorta(row.fecha)}
          </MDTypography>
        </MDBox>
      ),
      peaje: (
        <MDBox
          data-subtotal={esSubtotal ? "true" : undefined}
          sx={{
            ...s,
            padding: 0,
            border: "none",
          }}
        >
          <MDTypography sx={{ ...s, padding: 0, border: "none", whiteSpace: "nowrap" }}>
            {row.peaje}
          </MDTypography>
        </MDBox>
      ),
      gopassPasosExitosos: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.gopassPasosExitosos, tipoFila)}
        </MDBox>
      ),
      concesion: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.valorConcesion, tipoFila)}
        </MDBox>
      ),
      diferenciaInicial: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.diferenciaInicial, tipoFila)}
        </MDBox>
      ),
      pagosRechazos: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.pagosRechazos, tipoFila)}
        </MDBox>
      ),
      totalGopass: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.totalGopass, tipoFila)}
        </MDBox>
      ),
      diferenciaFinal: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.diferenciaFinal, tipoFila)}
        </MDBox>
      ),
      pagoFinanciera: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {row.pagoFinops
            ? renderPagoFinops(row.pagoFinanciera, row.pagoFinopsCuadrado)
            : renderMoney(row.pagoFinanciera, tipoFila)}
        </MDBox>
      ),
      diferenciaFinanciera: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.diferenciaFinanciera, tipoFila)}
        </MDBox>
      ),
      valoresAdicionales: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.valoresAdicionales, tipoFila)}
        </MDBox>
      ),
      descuentos: (
        <MDBox sx={{ ...s, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(row.descuentos ?? row.descuento, tipoFila)}
        </MDBox>
      ),
      totalPasos: <MDTypography sx={{ ...s, p: 0 }}>{formatPasos(row.totalPasos)}</MDTypography>,
      observacion: esSubtotal ? (
        ""
      ) : (
        <MDTypography
          sx={{
            fontSize: 13,
            color: "text.primary",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {textoObservacion(row) || "—"}
        </MDTypography>
      ),
      acciones: (
        <>
          {!esSubtotal && (
            <Tooltip
              title={
                textoObservacion(row).trim()
                  ? "Esta fila ya tiene una observación guardada"
                  : "Añadir observación"
              }
            >
              <span>
                <IconButton
                  disabled={Boolean(textoObservacion(row).trim())}
                  aria-label={`Observación de ${row.peaje} del ${row.fecha}`}
                  size="small"
                  color={textoObservacion(row) ? "success" : "default"}
                  onClick={() => abrirModalObservacion(row)}
                >
                  <CommentIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      ),
    };
  });

  // Fila de TOTALES
  if (rows.length > 0 && totalesGenerales) {
    const tg = totalesGenerales;
    rows.push({
      totalPasos: (
        <MDTypography sx={{ ...totalCellStyle, p: 0 }}>{formatPasos(tg.totalPasos)}</MDTypography>
      ),
      observacion: "",
      dia: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "center" }}>
          <MDTypography sx={{ ...totalCellStyle, padding: 0, border: "none", fontSize: "0.75rem" }}>
            Total
          </MDTypography>
        </MDBox>
      ),
      peaje: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none" }}>
          <MDTypography
            sx={{ ...totalCellStyle, padding: 0, border: "none", whiteSpace: "nowrap" }}
          >
            TOTALES
          </MDTypography>
        </MDBox>
      ),
      gopassPasosExitosos: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.gopassPasosExitosos, "total")}
        </MDBox>
      ),
      concesion: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.valorConcesion, "total")}
        </MDBox>
      ),
      diferenciaInicial: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.diferenciaInicial, "total")}
        </MDBox>
      ),
      pagosRechazos: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.pagosRechazos, "total")}
        </MDBox>
      ),
      totalGopass: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.totalGopass, "total")}
        </MDBox>
      ),
      diferenciaFinal: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.diferenciaFinal, "total")}
        </MDBox>
      ),
      pagoFinanciera: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {tg.pagoFinops
            ? renderPagoFinops(tg.pagoFinanciera, tg.pagoFinopsCuadrado)
            : renderMoney(tg.pagoFinanciera, "total")}
        </MDBox>
      ),
      diferenciaFinanciera: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.diferenciaFinanciera, "total")}
        </MDBox>
      ),
      valoresAdicionales: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.valoresAdicionales, "total")}
        </MDBox>
      ),
      descuentos: (
        <MDBox sx={{ ...totalCellStyle, padding: 0, border: "none", textAlign: "right" }}>
          {renderMoney(tg.descuentos, "total")}
        </MDBox>
      ),
    });
  }

  const totalRow = rows.length && totalesGenerales ? rows.pop() : null;

  // ------------------------------------------------------------
  // RESUMEN POR PEAJE
  // ------------------------------------------------------------
  const columnsResumen = [
    { Header: "Peaje", accessor: "peaje", width: "40%", Cell: ({ value }) => value },
    {
      Header: "Total pasos",
      accessor: "totalPasos",
      width: "20%",
      align: "right",
      Cell: ({ value }) => value,
    },
    {
      Header: "Total Rechazado",
      accessor: "totalRechazado",
      width: "20%",
      align: "right",
      Cell: ({ value }) => value,
    },
    {
      Header: "Total Gopass",
      accessor: "totalPago",
      width: "20%",
      align: "right",
      Cell: ({ value }) => value,
    },
  ];

  const rowsResumen = datosResumen.map((row) => ({
    totalPasos: (
      <MDTypography sx={{ fontSize: 13, fontWeight: 600 }}>
        {formatPasos(row.totalPasos)}
      </MDTypography>
    ),
    peaje: (
      <MDTypography variant="body2" fontWeight="medium" sx={{ fontSize: "0.82rem" }}>
        {row.peaje}
      </MDTypography>
    ),
    totalRechazado: (
      <MDTypography variant="body2" fontWeight="medium" color="error" sx={{ fontSize: "0.82rem" }}>
        {formatCurrency(row.totalRechazado)}
      </MDTypography>
    ),
    totalPago: (
      <MDTypography
        variant="body2"
        fontWeight="bold"
        color="success.main"
        sx={{ fontSize: "0.82rem" }}
      >
        {formatCurrency(row.totalPago)}
      </MDTypography>
    ),
  }));

  useImperativeHandle(ref, () => ({
    refresh: handleBuscar,
  }));

  return (
    <MDBox pt={0} pb={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={12}>
          <Card>
            <HeaderSection
              title="Gestión de conciliaciones"
              subtitle={
                appliedFilters && showResults && !error
                  ? `${appliedFilters.concesion} · ${
                      appliedFilters.peaje || "Todos los peajes"
                    } · ${appliedFilters.inicio} al ${appliedFilters.fin}`
                  : "Consulta y cuadre de peajes por concesión"
              }
              onExport={handleExport}
              canExport={!loading && !error && showResults && datosTabla.length > 0}
            />

            <FilterSection
              concesiones={concesiones}
              peajes={peajes}
              selectedConcesion={selectedConcesion}
              selectedPeaje={selectedPeaje}
              selectedFechaInicio={selectedFechaInicio}
              selectedFechaFin={selectedFechaFin}
              setSelectedConcesion={setSelectedConcesion}
              setSelectedPeaje={setSelectedPeaje}
              setSelectedFechaInicio={setSelectedFechaInicio}
              setSelectedFechaFin={setSelectedFechaFin}
              showResults={showResults}
              handleBuscar={handleBuscar}
              handleLimpiarTodo={handleLimpiarTodo}
              loadingPeajes={loadingPeajes}
              loading={loading}
            />

            <MDBox p={2}>
              {loading && (
                <Box role="status" sx={{ p: 2 }}>
                  <MDTypography variant="body2">Consultando conciliaciones…</MDTypography>
                  {[1, 2, 3, 4].map((key) => (
                    <Skeleton key={key} height={60} />
                  ))}
                </Box>
              )}
              {error && !loading && (
                <Alert
                  severity="error"
                  action={
                    <MDButton color="error" variant="text" onClick={handleBuscar}>
                      Reintentar
                    </MDButton>
                  }
                >
                  {error}
                </Alert>
              )}
              {!loading && !error && !showResults && (
                <Box p={4} textAlign="center">
                  <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
                  <MDTypography variant="h5" fontWeight="bold" gutterBottom>
                    Seleccione filtros y busque
                  </MDTypography>
                  <MDTypography variant="body1" color="text.secondary">
                    Seleccione una concesión, opcionalmente un peaje y un rango de fechas, luego
                    haga clic en Buscar
                  </MDTypography>
                </Box>
              )}

              {!loading && !error && showResults && datosTabla.length > 0 && (
                <>
                  <Paper variant="outlined" sx={{ overflow: "hidden", borderRadius: 2, mb: 3 }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      gap={1}
                      sx={{ p: 2 }}
                    >
                      <MDTypography variant="h6">Detalle de conciliaciones</MDTypography>
                      <MDTypography variant="caption">
                        {datosTabla
                          .filter((row) => row.tipoFila !== "subtotal")
                          .length.toLocaleString("es-CO")}{" "}
                        registros · Desliza para ver todas las columnas
                      </MDTypography>
                    </Stack>
                    <TableContainer sx={reconciliationTableSx}>
                      <Table
                        stickyHeader
                        size="small"
                        aria-label="Detalle de conciliaciones"
                        sx={{ minWidth: 2300 }}
                      >
                        <TableHead>
                          <TableRow>
                            {columns.map((column) => (
                              <TableCell
                                key={column.accessor}
                                align={
                                  column.align ||
                                  (["dia", "peaje", "acciones"].includes(column.accessor)
                                    ? "left"
                                    : "right")
                                }
                              >
                                {column.Header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(pageSize === -1
                            ? rows
                            : rows.slice(page * pageSize, (page + 1) * pageSize)
                          ).map((row, index) => (
                            <TableRow key={`${row.fecha}-${row.peaje}-${index}`} hover>
                              {columns.map((column) => (
                                <TableCell key={column.accessor}>{row[column.accessor]}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                        {totalRow && (
                          <TableFooter>
                            <TableRow>
                              {columns.map((column) => (
                                <TableCell key={column.accessor}>
                                  {totalRow[column.accessor]}
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableFooter>
                        )}
                      </Table>
                    </TableContainer>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                    >
                      <MDTypography variant="caption" sx={{ px: 2.5, py: 1 }}>
                        Filas por página: 200
                      </MDTypography>
                      <TablePagination
                        component="div"
                        count={rows.length}
                        page={
                          pageSize === -1
                            ? 0
                            : Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1))
                        }
                        rowsPerPage={pageSize}
                        rowsPerPageOptions={[200]}
                        onPageChange={(_, value) => setPage(value)}
                        onRowsPerPageChange={(event) => {
                          setPageSize(Number(event.target.value));
                          setPage(0);
                        }}
                        labelRowsPerPage="Filas por página"
                        labelDisplayedRows={({ from, to, count }) =>
                          `${from}–${to} de ${count} filas (incluye subtotales)`
                        }
                        sx={{ "& .MuiTablePagination-toolbar": { flexWrap: "wrap" } }}
                      />
                    </Stack>
                  </Paper>

                  {/* RESUMEN POR PEAJE */}
                  {datosResumen.length > 0 && (
                    <>
                      <MDTypography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        sx={{ mt: 3, fontSize: "1rem", color: "#1e293b" }}
                      >
                        Resumen de Totales
                      </MDTypography>
                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: "10px",
                          overflow: "hidden",
                          border: "1px solid #e0e0e0",
                          mb: 3,
                        }}
                      >
                        <DataTable
                          table={{ columns: columnsResumen, rows: rowsResumen }}
                          isSorted={false}
                          entriesPerPage={entriesConfig}
                          showTotalEntries={true}
                          noEndBorder={true}
                          canSearch={false}
                          pagination={{ variant: "gradient", color: "primary" }}
                          sx={{
                            "& .MuiTableCell-root": {
                              fontSize: "0.82rem",
                              padding: "8px 12px",
                              backgroundColor: `${CREMA_FONDO} !important`,
                            },
                            "& .MuiTableCell-head": {
                              backgroundColor: `${CREMA_HEADER} !important`,
                              fontWeight: 800,
                              color: "#78350f",
                              borderBottom: "2px solid #f59e0b !important",
                              fontSize: "0.72rem",
                              textTransform: "uppercase",
                              letterSpacing: "0.4px",
                            },
                          }}
                        />
                      </Paper>
                    </>
                  )}

                  {/* BLOQUE DE DIFERENCIAS */}
                  {resumenDiferencias.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: "10px",
                        overflow: "hidden",
                        border: `1px solid ${AMBAR_BORDE}`,
                        backgroundColor: CREMA_FONDO,
                        mt: 2,
                      }}
                    >
                      <Box
                        sx={{
                          px: 2.5,
                          py: 1.4,
                          backgroundColor: CREMA_HEADER,
                          borderBottom: `2px solid ${AMBAR_BORDE}`,
                        }}
                      >
                        <MDTypography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 800,
                            color: "#78350f",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Diferencias y Conciliación
                        </MDTypography>
                      </Box>

                      <Box sx={{ p: 0 }}>
                        {resumenDiferencias.map((d, idx) => {
                          const esUltima = idx === resumenDiferencias.length - 1;
                          const esNegativo = d.value < 0;
                          return (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                                flexWrap: "wrap",
                                px: 2.5,
                                py: 1.1,
                                borderBottom: esUltima ? "none" : `1px solid ${CREMA_BORDER}`,
                                backgroundColor: idx % 2 === 0 ? CREMA_FONDO : "#fdf3d0",
                                transition: "background-color 0.15s",
                                "&:hover": { backgroundColor: "#fde68a" },
                              }}
                            >
                              <MDTypography
                                sx={{
                                  fontSize: "0.82rem",
                                  color: TEXTO_SUBTOTAL,
                                  fontWeight: 600,
                                }}
                              >
                                {d.label}
                                {d.minuendo !== undefined && (
                                  <MDTypography
                                    component="span"
                                    display="block"
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 400,
                                      color: "#64748b",
                                      mt: 0.5,
                                    }}
                                  >
                                    {formatCurrency(d.minuendo)} − ({formatCurrency(d.sustraendo)})
                                  </MDTypography>
                                )}
                              </MDTypography>
                              <MDTypography
                                sx={{
                                  fontSize: "0.9rem",
                                  fontWeight: 800,
                                  color: esNegativo ? "#c62828" : TEXTO_TOTAL,
                                  fontVariantNumeric: "tabular-nums",
                                }}
                              >
                                {formatCurrency(d.value)}
                              </MDTypography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Paper>
                  )}
                </>
              )}

              {!loading && !error && showResults && datosTabla.length === 0 && (
                <Box p={4} textAlign="center">
                  <Search sx={{ fontSize: 60, color: "text.disabled", mb: 2 }} />
                  <MDTypography variant="h5" fontWeight="bold" gutterBottom>
                    No se encontraron resultados
                  </MDTypography>
                  <MDTypography variant="body1" color="text.secondary">
                    No hay datos para la concesión, peaje y fechas seleccionadas.
                  </MDTypography>
                  <Box display="flex" justifyContent="center" gap={2} mt={2}>
                    <MDButton
                      variant="contained"
                      color="primary"
                      onClick={handleLimpiarTodo}
                      startIcon={<RefreshIcon />}
                      sx={{ borderRadius: "8px" }}
                    >
                      Limpiar Filtros
                    </MDButton>
                  </Box>
                </Box>
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(avisoObservacion)}
        autoHideDuration={6000}
        onClose={() => setAvisoObservacion("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setAvisoObservacion("")}>
          {avisoObservacion}
        </Alert>
      </Snackbar>
      {/* MODAL OBSERVACIÓN */}
      <Dialog open={observacionModal} onClose={cerrarModalObservacion} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            backgroundColor: "#1e293b",
            color: "#f1f5f9",
          }}
        >
          <MDTypography
            component="span"
            variant="h6"
            fontWeight="bold"
            sx={{ color: "#f1f5f9", fontSize: "1rem" }}
          >
            {textoObservacion(filaSeleccionada) ? "Editar observación" : "Añadir observación"}
          </MDTypography>
          <IconButton
            onClick={cerrarModalObservacion}
            disabled={guardandoObservacion}
            aria-label="Cerrar observación"
            size="small"
            sx={{ color: "#f1f5f9" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            La observación se guardará para la concesión, fecha y peaje de esta fila.
          </Alert>
          {errorObservacion && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorObservacion}
            </Alert>
          )}
          {filaSeleccionada && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={3}
              sx={{ mb: 3, p: 2, bgcolor: "#f4f7fa", borderRadius: 2 }}
            >
              <Box>
                <MDTypography variant="caption" color="text">
                  Concesión
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {filaSeleccionada.concesion}
                </MDTypography>
              </Box>
              <Box>
                <MDTypography variant="caption" color="text">
                  Fecha
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {formatFechaCorta(filaSeleccionada.fecha)}
                </MDTypography>
              </Box>
              <Box>
                <MDTypography variant="caption" color="text">
                  Peaje
                </MDTypography>
                <MDTypography variant="body2" fontWeight="medium">
                  {filaSeleccionada.peaje}
                </MDTypography>
              </Box>
            </Stack>
          )}

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observación"
            placeholder="Escriba aquí su observación..."
            disabled={guardandoObservacion}
            value={observacionTexto}
            onChange={(e) => setObservacionTexto(e.target.value)}
            variant="outlined"
            size="small"
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="text"
            color="secondary"
            onClick={cerrarModalObservacion}
            disabled={guardandoObservacion}
            sx={{ textTransform: "none" }}
          >
            Cancelar
          </MDButton>
          <MDButton
            variant="contained"
            onClick={guardarObservacion}
            disabled={guardandoObservacion || !observacionTexto.trim()}
            sx={{
              textTransform: "none",
              backgroundColor: "#1e293b",
              color: "#ffffff",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#0f172a" },
            }}
          >
            {guardandoObservacion ? "Guardando…" : "Guardar"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
});

TablaGestionConciliaciones.propTypes = {
  refreshFlag: PropTypes.bool,
};

TablaGestionConciliaciones.defaultProps = {
  refreshFlag: false,
};

export default TablaGestionConciliaciones;
